import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { achievementResourceType } from "@/modules/achievements/api/achievementResourceConsts";
import { AchievementResource } from "@/modules/achievements/api/achievementResourceStruct";
import { useJsonApi } from "@/modules/api/hooks/useJsonApi";
import { queryKeys } from "@/modules/api/queryKeys";
import { apiErrorValidate } from "@/modules/api/utils/apiErrorValidate";
import { useAuthContext } from "@/modules/auth/hooks/useAuthContext";
import { groupsResourceType } from "@/modules/groups/api/resources/groupsResourceConsts";
import { GroupItemResource } from "@/modules/groups/api/resources/groupsResourceStruct";
import {
  getUserQuery,
  GetUserResponse,
} from "@/modules/user/api/queries/getUserQuery";
const translationNamespace = "apiMessages/useGetUserQuery";

export function useGetUserQuery() {
  const jsonApi = useJsonApi();
  const { userCompanyId, userId } = useAuthContext();
  const { t } = useTranslation(translationNamespace);
  return useQuery(
    userId && userCompanyId
      ? {
          queryKey: [queryKeys.user.getUser, userId, userCompanyId],
          queryFn: getUserQuery(jsonApi, {
            companyId: userCompanyId,
            userId,
          }),
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          select: (response) => {
            const {
              data: { id, attributes, meta },
              included,
            } = response;

            return {
              id,
              attributes,
              groups: parseGroups(included),
              achievements: parseAchievements(included),
              meta,
            };
          },
          onError: (error) => {
            apiErrorValidate(error, {
              401: {
                UNAUTHORIZED: t(
                  "You are not allowed to view the user. Please sign in again"
                ),
              },
              403: {
                ACCESS_FORBIDDEN: t(
                  "You don't have an access to view the user"
                ),
              },
              404: {
                USER_NOT_FOUND: t("The user cannot be found"),
              },
            });
          },
        }
      : {
          enabled: false,
        }
  );
}

function parseGroups(included: GetUserResponse["included"]) {
  return included
    .filter(
      (item): item is GroupItemResource => item.type === groupsResourceType
    )
    .map(({ id, attributes }) => ({
      id,
      name: attributes.name,
      colorHex: attributes.colorHex,
    }));
}

function parseAchievements(included: GetUserResponse["included"]) {
  return included
    .filter(
      (item): item is AchievementResource =>
        item.type === achievementResourceType
    )
    .map(({ id, attributes, relationships }) => ({
      achievementId: id,
      groupId: relationships.userGroup.data.id,
      levelOrder: attributes.levelOrder,
      pointsNumber: attributes.pointsNumber,
      averageRating: attributes.averageRating,
    }));
}
